import { OrderBookRealTimeClient } from '@katanaperps/katana-perps-sdk';
import { useEffect, useState } from 'react';

import { ApiLevelTwoOrderBookRow } from '#/api/types';
import { DEPTH_CHART_ROWS_LIMIT } from '#/components/chart/depth/chartConfig';
// import OrderBookRealTimeClientDebugger from './OrderBookRealTimeClientDebugger';
import config from '#/config';
import { EXCHANGE_DECIMALS } from '#/config/constants';
import { waitAsync } from '#/lib/promiseTools';
import { throttle } from '#/lib/throttle';
import { filterFalsy } from '#/lib/utils';
import { updateOrderbookWebsocketState } from '#/reduxState/exchange/websocketStateReducer';
import { calculateTickOrStepSize } from '#/reduxState/markets/listed/listedMarketsUtils';
import { staticDispatch } from '#/reduxState/store';

import {
  RowsType,
  calculateSpread,
  LevelTwoOrderBookRow,
  prepareTransformApiOrderBookRow,
} from './orderBookServiceUtils';

const katanaEVMConfig = config.chains.katanaChain;

export function createOrderbookClient() {
  const orderbook = new OrderBookRealTimeClient(
    {
      baseRestApiURL: katanaEVMConfig.restApiUrl,
      baseWebSocketURL: katanaEVMConfig.webSocketApiUrl,
      sandbox: config.envIsSandbox,
      // TODO: remove from SDK ? and only allow to set manually on getL2
      // Note: We are always providing tick-size while getting book
      marketsResponse: [],
    }
    // ratesOverride
  );
  return orderbook;
}

type SetOrdersFuntion = (
  asks: RowsType,
  bids: RowsType,
  reset: boolean
) => void;

class InternalOrderBookService {
  public marketSymbol: string;

  private defautlTickSizeVisibleDecimal: number;
  private defaultTickSizeInPips: bigint;

  // Custom defined tick size (or default) - eg. to change tickSize levels in orderbook
  private customTickSizeInPips?: bigint;
  private customTickSizeVisibleDecimals?: number;
  private stepSizeVisibleDecimals?: number;

  private realtimeClient: OrderBookRealTimeClient;

  private orderbookReactHook?: SetOrdersFuntion;
  private depthChartReactHook?: SetOrdersFuntion;
  // Note: Also TPSL subscribe on it's own connection

  private refreshInterval?: number;
  private asyncUpdateRequired = false;
  private updating = false;

  public smallestAsk: LevelTwoOrderBookRow | undefined = undefined;
  public largestBid: LevelTwoOrderBookRow | undefined = undefined;

  constructor(
    currentMarketPair: string,
    // ratesOverride: RatesOverride,
    defaultTickSizeInPips: bigint,
    defautlTickSizeVisibleDecimal: number
  ) {
    this.marketSymbol = currentMarketPair;
    this.defaultTickSizeInPips = defaultTickSizeInPips;
    this.defautlTickSizeVisibleDecimal = defautlTickSizeVisibleDecimal;
    this.realtimeClient = createOrderbookClient();
  }

  public getL2AsksAndBids() {
    return this.realtimeClient.getOrderBookL2(
      this.marketSymbol,
      DEPTH_CHART_ROWS_LIMIT,
      this.defaultTickSizeInPips
    );
  }

  public setDepthChartReactHook(
    onChange?: (
      asks: ApiLevelTwoOrderBookRow[],
      bids: ApiLevelTwoOrderBookRow[]
    ) => void
  ) {
    if (!onChange) {
      this.depthChartReactHook = undefined;
      return;
    }
    const processAsync = async () => {
      const { asks, bids } = await this.getL2AsksAndBids();
      onChange(asks, bids);
    };
    this.depthChartReactHook = throttle(processAsync, 1700);

    // Run to get initial data
    window.setTimeout(processAsync);
  }

  public startOrderbookRefreshProcess() {
    this.refreshInterval = window.setInterval(() => {
      if (this.updating) {
        // Lets wait until refresh process is done
        return;
      }
      if (this.asyncUpdateRequired) {
        // Keep information about next orderbook refresh,
        // but wait until current one is received to prevent
        // performance issues where web client cannot catch up
        // with large amount pace of new messages
        this.runUpdate().finally(() => {
          this.asyncUpdateRequired = false;
        });
      }
    }, 400);
  }

  private runUpdate() {
    this.updating = true;
    return (
      this.fetchAndReplaceOrderbook()
        // Add small delay between calculations
        .then(() => waitAsync(100))
        .finally(() => {
          this.updating = false;
        })
    );
  }

  public scheduleUpdate() {
    if (this.updating) {
      this.asyncUpdateRequired = true;
      return;
    }
    this.runUpdate();
  }

  public async setOrderbookMainHook(
    customTickSizeInDecimals: string | number,
    stepSizeVisibleDecimals: number,
    onSetOrders: SetOrdersFuntion | undefined,
    shouldWipeData: boolean
  ) {
    const customTickSize = calculateTickOrStepSize(
      customTickSizeInDecimals.toString()
    );
    this.customTickSizeVisibleDecimals = customTickSize.visibleDecimals;
    this.customTickSizeInPips = BigInt(customTickSize.inPip);
    this.stepSizeVisibleDecimals = stepSizeVisibleDecimals;
    if (shouldWipeData) {
      onSetOrders?.([], [], true);
      this.largestBid = undefined;
      this.smallestAsk = undefined;
    }
    this.orderbookReactHook = onSetOrders
      ? throttle(onSetOrders, 500)
      : undefined;
    if (!shouldWipeData) {
      this.scheduleUpdate();
    }
  }

  public async unsetOrderbookMainHook() {
    this.orderbookReactHook = undefined;
  }

  public async register() {
    this.startOrderbookRefreshProcess();

    this.realtimeClient.on('l2', () => {
      this.scheduleUpdate();
    });
    this.realtimeClient.on('ready', () => {
      console.log('[Orderbook] Ready', this.marketSymbol);
      this.scheduleUpdate();
      changeService(this, true);
      staticDispatch(updateOrderbookWebsocketState(true));
    });

    this.realtimeClient.on('disconnected', () => {
      console.warn('[Orderbook] disconnected');
      staticDispatch(updateOrderbookWebsocketState(false));
    });
    this.realtimeClient.on('error', error => {
      if (error?.message?.includes('Missing l2 update sequence')) {
        // This can happen occasionally - make it less noisy
        console.warn('[Orderbook] warn', error?.message);
      } else {
        console.warn('[Orderbook] error', error);
      }
      staticDispatch(updateOrderbookWebsocketState(false));
    });
    console.log('[Orderbook] Start', this.marketSymbol);
    await this.realtimeClient.start([this.marketSymbol]);
  }

  private async fetchAndReplaceOrderbook() {
    const transformRowAtFullPrecision = prepareTransformApiOrderBookRow(
      this.defautlTickSizeVisibleDecimal,
      this.stepSizeVisibleDecimals ?? EXCHANGE_DECIMALS
    );
    const customTickSize =
      this.defaultTickSizeInPips !== this.customTickSizeInPips;

    if (customTickSize) {
      // Even when user using custom tick-size, we still want to use
      // full resolution for determining orderbook middle price
      const middleOrders = await this.realtimeClient.getOrderBookL2(
        this.marketSymbol,
        2,
        BigInt(this.defaultTickSizeInPips)
      );
      this.smallestAsk = middleOrders.asks.length
        ? transformRowAtFullPrecision(middleOrders.asks[0])
        : undefined;
      this.largestBid = middleOrders.bids.length
        ? transformRowAtFullPrecision(middleOrders.bids[0])
        : undefined;
    }

    if (this.customTickSizeVisibleDecimals === undefined) {
      return;
    }
    const transformRow = prepareTransformApiOrderBookRow(
      this.customTickSizeVisibleDecimals,
      this.stepSizeVisibleDecimals ?? EXCHANGE_DECIMALS
    );
    const { asks, bids } = await this.realtimeClient.getOrderBookL2(
      this.marketSymbol,
      // TODO: make it more efficient by rebuilding orderbook based on height / mobile
      //        but careful if full size is not needed anywhere
      // Note: We only need ~12 on Desktop, more on Mobile
      100,
      this.customTickSizeInPips
    );

    if (!customTickSize) {
      this.smallestAsk = asks.length
        ? transformRowAtFullPrecision(asks[0])
        : undefined;
      this.largestBid = bids.length
        ? transformRowAtFullPrecision(bids[0])
        : undefined;
    }

    const asksTransformed = asks.map(row => transformRow(row)).reverse();
    const bidsTransformed = bids.map(row => transformRow(row));

    if (this.orderbookReactHook) {
      this.orderbookReactHook(asksTransformed, bidsTransformed, false);
    }
    if (this.depthChartReactHook) {
      this.depthChartReactHook(asksTransformed, bidsTransformed, false);
    }
    // Make sure we dont block main thread for a long time
    await waitAsync();
  }

  public getMaximumTickSizeUnderSpread(market: string): Promise<bigint> {
    return this.realtimeClient.getMaximumTickSizeUnderSpread(market);
  }

  public unregister() {
    console.log('[Orderbook] Unregister', this.marketSymbol);
    clearInterval(this.refreshInterval);
    this.orderbookReactHook = undefined;
    this.depthChartReactHook = undefined;
    // this.realtimeClient.removeAllListeners();
    // this.realtimeClient.on('error', error => {
    //   console.log(
    //     'Received error from stopped orderbook client',
    //     error?.message
    //   );
    // });
    const client = this.realtimeClient;
    client.stop();
    client.removeAllListeners();
    // TODO: SDK bug cause `.stop` to not work in reconnecting state
    client.on('connected', () => {
      console.warn('Stopped connection reopened - force closing');
      client.stop();
    });
    // this.realtimeClient.on('error', error => {
    //   console.log(
    //     'Received error from stopped orderbook client',
    //     error?.message
    //   );
    // });
    // TODO: ?
    // this.realtimeClient = undefined;
  }
}

let cachedService: InternalOrderBookService | undefined = undefined;

type ServiceCallback = (ready: boolean, market: string) => void;

const changedServiceCallbacks: {
  [key: string]: ServiceCallback;
} = {};

function changeService(
  service: InternalOrderBookService | undefined,
  ready = false
) {
  cachedService = service;
  const callbacks = Object.keys(changedServiceCallbacks)
    .map(key => changedServiceCallbacks[key])
    .filter(filterFalsy) as ServiceCallback[];
  if (callbacks.length > 0) {
    callbacks.forEach(cb => cb && cb(ready, service?.marketSymbol || ''));
  }
  return service;
}

/**
 * TODO: setting hooks can be done without waiting for orderbook - not async
 * Use this function to connect to existing orderbook.
 *  - Orderbook must be already running.
 *  - Can be only used once per connection type (depth, orderbook, )
 */
export const connectToOrderbookAsync = async (
  market: string,
  signal: AbortSignal,
  connection:
    | {
        type: 'orderbook';
        customTickSizeInDecimals: string;
        defaultMarketTickSizeInPip: number;
        stepSizeVisibleDecimals: number;
        onChange: SetOrdersFuntion;
      }
    | {
        type: 'depthChart';
        onChange: (
          asks: ApiLevelTwoOrderBookRow[],
          bids: ApiLevelTwoOrderBookRow[]
        ) => void;
      }
) => {
  while (!cachedService || cachedService.marketSymbol !== market) {
    if (signal.aborted) {
      return;
    }
    console.warn('[orderbookService] Service not ready - retrying');
    await waitAsync(500);
  }
  if (signal.aborted) {
    return;
  }
  switch (connection.type) {
    case 'orderbook':
      cachedService.setOrderbookMainHook(
        connection.customTickSizeInDecimals,
        connection.stepSizeVisibleDecimals,
        connection.onChange,
        false
      );
      break;
    case 'depthChart':
      cachedService.setDepthChartReactHook(connection.onChange);
      break;
  }
};

export const disconnectFromOrderbook = (
  connectionType: 'orderbook' | 'depthChart',
  abortController: AbortController
) => {
  abortController.abort();
  switch (connectionType) {
    case 'orderbook':
      cachedService?.unsetOrderbookMainHook();
      break;
    case 'depthChart':
      cachedService?.setDepthChartReactHook(undefined);
      break;
  }
};

/**
 * Unsafe because "connect" is async - TODO: orderbook fix this.
 * Use only for known state transitions eg. market change.
 * Using in lot of rerenders might cause app to get into buggy state.
 */
// export const unsafe_disconnectDepthChartFromOrderBookService = () => {
//   cachedService?.setDepthChartReactHook(undefined);
// };

export const registerOrderBookService = (
  currentMarketPair: string,
  defaultMarketTickSizeInPip: number,
  defautlTickSizeVisibleDecimal: number
) => {
  const reuseService = cachedService?.marketSymbol === currentMarketPair;
  if (!reuseService && cachedService) {
    // Market switched case
    cachedService.unregister();
  }
  if (!reuseService || !cachedService) {
    changeService(
      new InternalOrderBookService(
        currentMarketPair,
        // ratesOverride,
        BigInt(defaultMarketTickSizeInPip),
        defautlTickSizeVisibleDecimal
      )
    );
  }
  if (!cachedService) {
    throw new Error('Internal orderbook error');
  }
  // cachedService.setup(
  //   tickSizeInDecimals,
  //   marketStepSizeDecimals,
  //   setOrders,
  //   !reuseService
  // );
  // Recreate if only tick size or overrides changed
  const started = reuseService ? true : cachedService.register();
  return {
    readyPromise: started,
    unregister: () => {
      console.debug('Unregistering orderbook service');
      if (cachedService) {
        cachedService.unregister();
        // Allow GC to free previous Orderbook from memory
        changeService(undefined);
      }
    },
  };
};

export const getBestPriceFromDisplayedOrderbook = (
  type: 'bid' | 'ask'
): string => {
  if (!cachedService) {
    console.warn('getBestPrice: Orderbook not available');
    return '0';
  }
  const order =
    type === 'ask' ? cachedService.smallestAsk : cachedService.largestBid;
  return order ? order.price : '0';
};

export const getSpreadPercentageFromDisplayedOrderbook = (): number | null => {
  const spread = getSpreadFromDisplayedOrderbook();
  if (!spread) {
    return spread;
  }
  return spread.spreadPercentage;
};

export const getSpreadFromDisplayedOrderbook = (
  _asksOnlyToTriggerRerender?: LevelTwoOrderBookRow[],
  _bidsOnlyToTriggerRerender?: LevelTwoOrderBookRow[]
) => {
  if (!cachedService) {
    console.log('WARNING: Orderbook not available (GetSpreadPercentage)');
    return null;
  }
  if (!cachedService.smallestAsk || !cachedService.largestBid) {
    return null;
  }
  return calculateSpread(
    [cachedService.smallestAsk],
    [cachedService.largestBid]
  );
};

/** Request asks&bids information from orderbook */
export const fetchOrdersFromDisplayedOrderbook = () => {
  if (!cachedService) {
    console.log('WARNING: Orderbook not available (getOrders)');
    return undefined;
  }
  // TODO: check if unregistered ?
  return cachedService.getL2AsksAndBids();
};

let callbacksSequence = 0;

/** Hook which will tell us whenever Orderbook is currently available (displayed) */
export const useDisplayedOrderbook = () => {
  const [state, setState] = useState({
    serviceExist: !!cachedService,
    market: cachedService?.marketSymbol,
  });

  useEffect(() => {
    callbacksSequence += 1;
    const index = callbacksSequence;
    changedServiceCallbacks[index] = (service, market) =>
      setState({ serviceExist: !!service, market });
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete changedServiceCallbacks[index];
    };
  }, []);

  return {
    serviceExists: state.serviceExist,
    serviceMarket: state.market,
    // getAsksBids: () => cachedService?.getL2AsksAndBids(),
  };
};

// export const getOrderbookService = () => cachedService;

/**
 * The client should only display tick size options in the selector that are possible
 * to render with the current order book. This is a subtle point, but see staging’s IDEX-ETH market
 * for an illustration. When the tick size is set to 0.00001, the bids are rendered

  In this case, selecting the 0.0001 tick size does not return anything as the SDK has been updated
  to reject the request. Since all of the existing price levels are below 0.0001, all of the bids would be
  rounded to a price of 0 at the 0.0001 tick size. Rather than show an empty bid side of the book, 
  the client should not offer 0.0001 and lower as tick selections in these cases.
 */
// export const useOrderbookMaximumTickSize = (market: string | undefined) => {
//   const { serviceExists, serviceMarket } = useDisplayedOrderbook();
//   const [maximumTickSize, setMaximumTickSize] = useState<number | undefined>(
//     undefined
//   );
//   useEffect(() => {
//     // Set as undefined until loaded
//     setMaximumTickSize(undefined);

//     if (!market || !serviceExists || serviceMarket !== market) {
//       return;
//     }
//     cachedService?.getMaximumTickSizeUnderSpread(market).then(tickSize => {
//       const maxTickSize = BigNumberJs(Number(tickSize));
//       setMaximumTickSize(
//         maxTickSize.dividedBy(10 ** EXCHANGE_DECIMALS).toNumber()
//       );
//     });
//   }, [market, serviceExists, serviceMarket]);

//   return maximumTickSize;
// };
